import { Close as CloseIcon } from '@mui/icons-material'
import { Box, Grid, IconButton, Modal, Tooltip, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { cloneDeep, isEmpty, isEqual, sortBy } from 'lodash'
import moment from 'moment'
import { useContext, useEffect, useRef, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import colorScheme from 'utils/functions/colorsOptions'
import { useFirstRender } from 'utils/hooks/useFirstRender'
import useForceRerender from 'utils/hooks/useForceRerender'
import LoadingSpinner from '../../../custom-components/LoadingSpinner'
import { IconThemeContext } from '../../../custom-components/context/IconThemesContext'
import { DEVICES_ACTIONS } from '../../../reducers/devicesReducer'
import DoformsRecordsActions from '../datagrid/DoformsRecordsActions'
import DoformsMapCanvas from './DoformsMapCanvas'
import DoformsMapFilters from './DoformsMapFilters'
import DoformsMapGrid from './DoformsMapGrid'
import { generateMapItemProps } from './mapHelpers'
import { useMapRecordQuery } from './helper/mapRecordQuery'

const useStyles = makeStyles(() => ({
	headingContainer: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-between',
		paddingLeft: '10px',
	},
	headingContent: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-start',
		alignItems: 'baseline',
	},
	headingLabel: {
		display: 'block',
		paddingLeft: '10px',
		minHeight: '32px',
	},
	headingAction: {
		display: 'flex',
		alignItems: 'center',
	},
	dataGrid: {
		'& .MuiButton-root': {
			textTransform: 'none',
		},
		'& .MuiDataGrid-footerContainer': {
			width: '100%',
			display: 'inline-flex',
			justifyContent: 'end',
			'& .MuiTablePagination-selectLabel': {
				marginTop: '1em',
			},
			'& .MuiTablePagination-displayedRows': {
				marginTop: '1em',
			},
		},
		'& .MuiDataGrid-columnHeaderTitle': {
			overflow: 'hidden',
			lineHeight: '20px',
			whiteSpace: 'normal',
			fontSize: '14px',
		},
	},
	formMap: {
		display: 'flex',
		flexDirection: 'column',
		flex: '1 1 auto',
		paddingTop: '10px',
		overflow: 'auto',
	},
	icon: (props) => ({
		color: props.color,
		'&:hover': {
			color: props.active.color,
			backgroundColor: 'transparent',
		},
	}),
	noMinHeight: {
		minHeight: '0px !important',
	},
}))

const DoformsMap = (props) => {
	const [t] = useTranslation('common')

	const { iconTheme } = useContext(IconThemeContext)
	const classes = useStyles(iconTheme)

	const {
		action,
		environment,
		viewData,
		title,
		formSelected,
		deviceSelected,
		tab,
		module,
		onHandleShowLoading,
		onHandleOpenFullScreenFormsTab,
		onHandleCloseMap,
	} = props
	const [showMap, setShowMap] = useState(false)
	const [loading, setLoading] = useState(false)
	const [recordName, setRecordName] = useState('')

	const sendDispatch = useDispatch()
	const formMapRef = useRef(null)

	const defaultActions = {
		poi: false,
		report: true,
		recenter: false,
		refresh: false,
		help: false,
		animate: false,
		device_formSelectedChangeRefresh: false,
	}

	const defaultDevicesDetailsGroup = [
		{ name: 'devices', label: t('common:filters.devices'), checked: true },
	]

	const defaultDevicesDetails = [
		{ name: 'tracking', label: t('common:filters.tracking'), checked: true },
		{ name: 'geofence', label: t('common:filters.geofence'), checked: false },
		{ name: 'dispatch', label: t('common:filters.dispatch'), checked: false },
		{ name: 'timesheet', label: t('common:misc.time'), checked: false },
		{ name: 'form', label: t('common:filters.form'), checked: false },
	]

	const defaultDevicesReports = [
		{ name: 'trip', label: t('common:filters.trip'), checked: false },
		{ name: 'stop', label: t('common:filters.stop'), checked: false },
		{ name: 'geofence', label: t('common:filters.geofence'), checked: false },
		{ name: 'timesheet', label: t('common:misc.time'), checked: false },
	]

	const defaultDevicesReportsGroup = [
		{ name: 'trip', label: t('common:filters.trip'), checked: false },
		{ name: 'stop', label: t('common:filters.stop'), checked: false },
		{ name: 'timesheet', label: t('common:misc.time'), checked: false },
	]

	const getDefaultDevicesDetails = (deviceSelected) => {
		if (deviceSelected && !deviceSelected.key) {
			return defaultDevicesDetailsGroup
		} else {
			return defaultDevicesDetails
		}
	}

	const getDefaultDevicesReports = (deviceSelected) => {
		if (deviceSelected && !deviceSelected.key) {
			return defaultDevicesReportsGroup
		} else {
			return defaultDevicesReports
		}
	}

	const defaultFormsDetails = [
		{ name: 'dispatch', label: t('common:filters.dispatch'), checked: true },
		{ name: 'form', label: t('common:filters.form'), checked: false },
	]

	const infoWindowDataSet =
		tab === 'forms'
			? ['device', 'time', 'status', 'description', 'accuracy', 'form', 'record', 'address']
			: tab === 'devices'
			? ['time', 'status', 'accuracy', 'address']
			: []

	const [dateValue, setDateValue] = useState(moment())
	const [selectedActions, setSelectedActions] = useState(defaultActions)
	const [selectedPlace, setSelectedPlace] = useState(null)
	const [selectedOption, setSelectedOption] = useState('detail')
	const [detailState, setDetailState] = useState(
		tab === 'forms' ? defaultFormsDetails : getDefaultDevicesDetails(deviceSelected)
	)
	const [reportState, setReportState] = useState(
		tab === 'devices' ? getDefaultDevicesReports(deviceSelected) : []
	)
	const [mapItemsLoaded, setMapItemsLoaded] = useState([])

	const [mapRecords, setMapRecords] = useState([])
	const shouldRecenter = useRef(true)
	const { poi, report, recenter, refresh, help, animate, device_formSelectedChangeRefresh } =
		selectedActions
	const { dispatch, form } = detailState

	const [infoWindowAction, setInfoWindowAction] = useState(null)
	const [actionRecord, setActionRecord] = useState(null)
	const [open, setOpen] = useState(false)
	const [state, setState] = useState(false)

	var isRestoreVariable = false
	useEffect(() => {
		if (action === null) return
		if (action !== 'map') {
			setShowMap(false)
			return
		}
		var newFullScreenDataStr = localStorage.getItem('newFullScreenData_map')
		if (newFullScreenDataStr !== null) {
			var newFullScreenData = JSON.parse(newFullScreenDataStr)
			if (newFullScreenData != null && newFullScreenData.mapData != null) {
				var mapData = newFullScreenData.mapData

				setShowMap(mapData.showMap)
				setDateValue(mapData.dateValue)
				setSelectedActions(mapData.selectedActions)
				setSelectedPlace(mapData.selectedPlace)
				setSelectedOption(mapData.selectedOption)
				setDetailState(mapData.detailState)
				setReportState(mapData.reportState)
				setMapItemsLoaded(mapData.mapItemsLoaded)
				setMapRecords(mapData.mapRecords)
				setInfoWindowAction(mapData.infoWindowAction)
				setActionRecord(mapData.actionRecord)
				setOpen(mapData.open)
				setState(mapData.state)

				/*
                setLoading(mapData.loading);
                */
				isRestoreVariable = true
			}

			localStorage.removeItem('newFullScreenData_map')
		}
	}, [])

	useEffect(() => {
		if (!module.openNewScreenMap) return
		onHandleOpenFullScreenTab()
		sendDispatch({
			type: DEVICES_ACTIONS.DEVICES_OPEN_NEW_SCREEN_MAP,
			payload: false,
		})
	}, [module.openNewScreenMap])

	const handleModalClose = () => {
		setLoading(false)
	}

	const showLoading = () => (
		<Modal
			open={loading}
			onClose={handleModalClose}
			aria-labelledby="modal-modal-title"
			aria-describedby="modal-modal-description"
		>
			<Box>
				<LoadingSpinner />
			</Box>
		</Modal>
	)

	const handleDate = (newValue) => {
		setMapItemsLoaded([])
		setMapRecords([])
		setDateValue(newValue)
	}

	const handledActions = (target) => {
		switch (target) {
			case 'poi':
				setSelectedActions({ ...selectedActions, poi: !poi })
				return
			case 'report':
				setSelectedActions({ ...selectedActions, report: !report })
				return
			case 'recenter':
				setSelectedActions({ ...selectedActions, recenter: !recenter })
				return
			case 'refresh':
				setSelectedActions({ ...selectedActions, refresh: !refresh })
				return
			case 'help':
				setSelectedActions({ ...selectedActions, help: !help })
				return
			case 'animate':
				setSelectedActions({ ...selectedActions, animate: !animate })
				return
			case 'view':
				setInfoWindowAction('view')
				setOpen(true)
				return
			case 'history':
				setInfoWindowAction('history')
				setOpen(true)
				return
			case 'device_formSelectedChangeRefresh':
				setSelectedActions({
					...selectedActions,
					device_formSelectedChangeRefresh: !device_formSelectedChangeRefresh,
				})
				return
			default:
				return
		}
	}

	const handledSelected = (target, isShowInfoWindow) => {
		if (!target) {
			setSelectedPlace(null)
			return
		}
		if (!isEmpty(selectedPlace) && target.key === selectedPlace.key) {
			if (isShowInfoWindow && selectedPlace.showInfoWindow) {
				setSelectedPlace(null)
				return
			} else if (!isShowInfoWindow) {
				setSelectedPlace(null)
				return
			}
		}

		let newSelectedPlace = cloneDeep(target)
		newSelectedPlace.showInfoWindow = isShowInfoWindow

		setSelectedPlace(newSelectedPlace)
	}

	const handledNewMapItemFetched = (target) => {
		if (!target) return
		if (!mapRecords.length) return
		shouldRecenter.current = false
		setMapRecords((prev) =>
			prev.map((rec) => {
				if (rec.key === target.key) {
					return target
				} else {
					return rec
				}
			})
		)
	}

	useEffect(() => {
		if (!selectedPlace) return
		const selectedPlaceKey = selectedPlace?.key
		const target = mapRecords.find((rec) => rec.key === selectedPlaceKey)
		if (!target || isEqual(selectedPlace, target)) return
		setSelectedPlace((prev) => {
			const newSelectedPlace = cloneDeep(prev)
			newSelectedPlace.addressLoading = target.addressLoading
			newSelectedPlace.address = target.address
			if (isEqual(prev, newSelectedPlace)) return prev
			return newSelectedPlace
		})
	}, [mapRecords])

	const handleDetails = (event) => {
		if (selectedOption === 'report') {
			/*mapItemsLoaded.forEach((stateName) => {
                if (stateName.includes('_report')) {
                    setMapItemsLoaded(mapItemsLoaded.filter(item => item !== stateName));
                    performUnCheckedRecords(stateName);
                }
            });*/
			setDetailState((prev) =>
				prev.map((item) => ({
					...item,
					loaded: false,
				}))
			)
			setSelectedOption('detail')
			return
		}
		if (event && !event.target.checked) {
			performUnCheckedRecords(event.target.name + '_detail')
		}
		setDetailState((prev) =>
			prev.map((item) => ({
				...item,
				checked: event && item.name === event.target.name ? event.target.checked : item.checked,
				loaded:
					event && item.name === event.target.name && event.target.checked ? false : item.loaded,
			}))
		)
		setSelectedOption('detail')
	}

	const handleReports = (event) => {
		if (selectedOption === 'detail') {
			/*mapItemsLoaded.forEach((stateName) => {
                if (stateName.includes('_detail')) {
                    setMapItemsLoaded(mapItemsLoaded.filter(item => item !== stateName));
                    performUnCheckedRecords(stateName);
                }
            });*/
			setReportState(
				reportState.map((item) => ({
					...item,
					loaded: false,
				}))
			)
			setSelectedOption('report')
			return
		}
		if (event && !event.target.checked) {
			performUnCheckedRecords(event.target.name + '_report')
		}
		setReportState(
			reportState.map((item) => ({
				...item,
				checked: event && item.name === event.target.name ? event.target.checked : item.checked,
				loaded:
					event && item.name === event.target.name && event.target.checked ? false : item.loaded,
			}))
		)
		setSelectedOption('report')
	}

	const setMapRocordsColor = (mapItems) => {
		let colorInd = 0
		let colors = colorScheme()
		for (let i = 0; i < mapItems.length; i++) {
			let item = mapItems[i]
			if (item.type === 'trip_report') {
				item.color = colors[colorInd]
				colorInd++
				if (colorInd >= colors.length) {
					colorInd = 0
				}
			}
		}
		return mapItems
	}

	const removeTypeFromRecords = (type) => {
		if (!mapRecords) return
		if (!mapRecords.length) return
		let result = mapRecords.filter((rec) => rec.type !== type)
		setMapRecords(result)
	}

	const performUnCheckedRecords = (stateName) => {
		switch (stateName) {
			case 'dispatch_detail':
				removeTypeFromRecords('event')
				break
			case 'form_detail':
				removeTypeFromRecords('location')
				break
			case 'devices_detail':
				removeTypeFromRecords('device')
				break
			case 'tracking_detail':
				removeTypeFromRecords('track')
				break
			case 'trip_report':
				removeTypeFromRecords('trip_report')
				break
			case 'stop_report':
				removeTypeFromRecords('stop_report')
				break
			case 'geofence_detail':
				removeTypeFromRecords('geofence')
				break
			case 'geofence_report':
				removeTypeFromRecords('geofence_report')
				break
			case 'timesheet_detail':
				removeTypeFromRecords('timesheet')
				break
			case 'timesheet_report':
				removeTypeFromRecords('timesheet_report')
				break
			default:
				removeTypeFromRecords(stateName)
				break
		}
	}

	const { allMapRecords, isLoading, isFetching, refetchData } = useMapRecordQuery({
		module,
		tab,
		formSelected,
		deviceSelected,
		detailState,
		reportState,
		dateValue,
		selectedOption,
	})

	useEffect(() => {
		setLoading(isLoading || isFetching)
	}, [isLoading, isFetching])

	useEffect(() => {
		if (selectedActions.refresh) {
			refetchData()
			setSelectedActions({ ...selectedActions, refresh: false })
		}
	}, [selectedActions.refresh])

	const stableAllMapRecords = useMemo(() => allMapRecords, [JSON.stringify(allMapRecords)])

	const stableSelectedOption = useMemo(() => selectedOption, [selectedOption])

	useEffect(() => {
		if (!stableSelectedOption) return

		if (!stableAllMapRecords || stableAllMapRecords.length === 0) {
			setMapRecords((prev) => {
				const isDifferent = JSON.stringify(prev) !== JSON.stringify([])

				if (isDifferent) {
					return []
				} else {
					return prev
				}
			})
			return
		}

		let newDataSet = computeAdditionalMapRecords(stableAllMapRecords)
		newDataSet = setMapRocordsColor(newDataSet)
		setMapRecords((prev) => {
			const isDifferent = JSON.stringify(prev) !== JSON.stringify(newDataSet)

			if (isDifferent) {
				return newDataSet
			} else {
				return prev
			}
		})
	}, [stableAllMapRecords, stableSelectedOption])

	useEffect(() => {
		if (isRestoreVariable) return
		let detail = tab === 'forms' ? defaultFormsDetails : getDefaultDevicesDetails(deviceSelected)
		if (detailState != null) {
			detail = detail.map((item) => {
				let itemFilter = detailState.filter(
					(itemFilter) => itemFilter.name == item.name && itemFilter.checked
				)
				if (itemFilter.length > 0) {
					item.checked = true
				}
				return item
			})
		}
		setDetailState(detail)

		let report = tab === 'devices' ? getDefaultDevicesReports(deviceSelected) : []
		if (reportState != null) {
			report = report.map((item) => {
				let itemFilter = reportState.filter(
					(itemFilter) => itemFilter.name == item.name && itemFilter.checked
				)
				if (itemFilter.length > 0) {
					item.checked = true
				}
				return item
			})
		}
		setReportState(report)
	}, [t('common:languages.dateFnsLocale')])

	useEffect(() => {
		if (isRestoreVariable) return
		setMapItemsLoaded([])
		setMapRecords([])
		setDetailState(tab === 'forms' ? defaultFormsDetails : getDefaultDevicesDetails(deviceSelected))
		setReportState(tab === 'devices' ? getDefaultDevicesReports(deviceSelected) : [])
		handledActions('device_formSelectedChangeRefresh')
	}, [deviceSelected, formSelected])

	useEffect(() => {
		if (isRestoreVariable) return
		if (selectedPlace) {
			setSelectedPlace(null)
		}
		if (!selectedActions.animate) return
		if (mapRecords) {
			setSelectedActions({ ...selectedActions, animate: true })
			let sortedMapItems = sortBy(mapRecords, ['time'], ['asc'])
			let index = 0
			let interval = setInterval(() => {
				let mapItem = sortedMapItems[index++]
				setSelectedPlace(mapItem)
				if (index === sortedMapItems.length) {
					setSelectedActions({ ...selectedActions, animate: false })
					clearInterval(interval)
				}
			}, 1000)
		}
	}, [selectedActions.animate])

	useEffect(() => {
		if (isRestoreVariable) return
		if (action === null) return
		if (action !== 'map') {
			setShowMap(false)
			return
		}
		setShowMap(true)
		if (module.dispatchRecord4Map != null) {
			let detail = tab === 'forms' ? defaultFormsDetails : getDefaultDevicesDetails(deviceSelected)
			detail = detail.map((item) => {
				item.checked = true
				return item
			})
			setDetailState(detail)
		} else if (module.mapData != null) {
			if (module.mapData.detailState != null) {
				setDetailState(module.mapData.detailState)
			}
			if (module.mapData.reportState != null) {
				setReportState(module.mapData.reportState)
			}
			onHandleShowLoading(false)
		}
	}, [action])

	const computeAdditionalMapRecords = (dataSet) => {
		if (!dataSet.length) return []
		let newDataSet = dataSet.map((item) => {
			return generateMapItemProps(item, environment, deviceSelected)
		})

		return newDataSet
	}

	const getTitle = () => {
		if (module.dispatchRecord4Map != null) {
			return recordName
		} else {
			return title
		}
	}

	const renderTitle = () => {
		if (isEmpty(formSelected)) return null
		const headingTitle = getTitle()
		return (
			<Grid container spacing={2}>
				<Grid item xs={12} className={classes.headingContainer}>
					<div className={classes.headingContent}>
						<Typography
							variant="h6"
							className={[classes.headingLabel, isEmpty(headingTitle) ? classes.noMinHeight : null]}
						>
							{headingTitle || ''}
							{/*{getSelectedProject(environment, formSelected).name + ` / ${formSelected.displayName}`}*/}
						</Typography>
					</div>
					<div className={classes.headingAction}>
						{(module.showMapCloseIcon || tab === 'views') && (
							<Tooltip
								title={`${t('tooltip.close')}`}
								arrow
								placement="bottom-start"
								disableInteractive
							>
								<span>
									<IconButton
										aria-label="Close map"
										size="small"
										color="primary"
										edge="end"
										onClick={() => handleCloseMap()}
									>
										<CloseIcon fontSize="inherit" className={classes.icon} />
									</IconButton>
								</span>
							</Tooltip>
						)}
					</div>
				</Grid>
			</Grid>
		)
	}

	const renderMap = () => {
		return (
			<>
				{renderTitle()}
				<Box ref={formMapRef} className={classes.formMap}>
					{showLoading()}
					<DoformsMapFilters
						environment={environment}
						dateValue={dateValue}
						selectedActions={selectedActions}
						detailState={detailState}
						reportState={reportState}
						onHandledDate={handleDate}
						onHandledActions={handledActions}
						onHandledDetails={handleDetails}
						onHandledReports={handleReports}
						selectedOption={selectedOption}
						tab={tab}
						module={module}
						onHandleOpenFullScreenTab={onHandleOpenFullScreenTab}
					/>
					<DoformsMapCanvas
						environment={environment}
						infoWindowDataSet={infoWindowDataSet}
						mapLoading={loading}
						mapRecords={mapRecords}
						shouldRecenter={shouldRecenter?.current}
						selectedActions={selectedActions}
						selectedPlace={selectedPlace}
						onHandledActions={handledActions}
						onHandledSelected={handledSelected}
						onHandledNewMapItemFetched={handledNewMapItemFetched}
						deviceSelected={deviceSelected}
						formMapRef={formMapRef}
					/>
					{selectedActions.report && (
						<DoformsMapGrid
							environment={environment}
							selectedPlace={selectedPlace}
							mapRecords={mapRecords}
							onHandledSelected={handledSelected}
							onHandledNewMapItemFetched={handledNewMapItemFetched}
							selectedOption={selectedOption}
							detailState={detailState}
							reportState={reportState}
							deviceSelected={deviceSelected}
							module={module}
							title={title}
						/>
					)}
				</Box>
			</>
		)
	}

	const handleClose = () => {
		setInfoWindowAction(null)
		setActionRecord(null)
		setOpen(false)
	}

	const onHandleOpenFullScreenTab = () => {
		var newFullScreenDataMap = {
			showMap: showMap,
			loading: loading,
			dateValue: dateValue,
			selectedActions: selectedActions,
			selectedPlace: selectedPlace,
			selectedOption: selectedOption,
			detailState: detailState,
			reportState: reportState,
			mapItemsLoaded: mapItemsLoaded,
			mapRecords: mapRecords,
			infoWindowAction: infoWindowAction,
			actionRecord: actionRecord,
			open: open,
			state: state,
		}

		localStorage.setItem('newFullScreenData_map', JSON.stringify({ mapData: newFullScreenDataMap }))
		onHandleOpenFullScreenFormsTab()
	}

	const handleCloseMap = () => {
		switch (tab) {
			case 'devices':
			case 'forms':
			case 'views':
				onHandleCloseMap()
				break
			default:
				break
		}
	}

	return (
		<>
			{showMap && renderMap()}
			{infoWindowAction && (
				<DoformsRecordsActions
					open={open}
					action={infoWindowAction}
					environment={environment}
					actionRecord={selectedPlace}
					formSelected={formSelected}
					name={viewData.name}
					tab={tab}
					onClose={handleClose}
					setLoading={setLoading}
				/>
			)}
		</>
	)
}

export default DoformsMap
